import React, { createContext, useReducer, ReactNode, useEffect } from "react";
import { GuestFormDataWithCompletion } from "../models/StaticDataModel";
import { getToken, isTokenExpired } from "../services/AuthService";
import { loginAndFetchInitialData } from "../services/LoginService";

// Define the shape of the state
export interface AppState {
    mainGuestId?: string | null;
    hotel: string;
    hotelId: string;
    reservationId: string;
    arrivalDate: string;
    departureDate: string;
    adultCount: number;
    childCount: number;
    roomType: string;
    user: GuestFormDataWithCompletion;
    guests: GuestFormDataWithCompletion[];
    selectedRooms: string[];
    currentStep: number;
    isFormValid: boolean;
    /** True when the currently open guest form has user-made changes that haven't been saved yet. */
    isFormDirty: boolean;
    paymentEnabled: boolean;
    titles: Record<string, string>;
    nationalities: Record<string, string>;
    countries: Record<string, string>;
}

const initialState: AppState = {
    mainGuestId: null,
    hotel: "",
    hotelId: "",
    reservationId: "",
    arrivalDate: "",
    departureDate: "",
    roomType: "",
    adultCount: 0,
    childCount: 0,
    user: {
        id: 1,
        title: "",
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "",
        dateOfBirth: { day: null, month: null, year: null },
        placeOfBirth: "",
        nationality: "",
        telephoneInfo: {
            id: "",
            type: "",
            phoneNumber: "",
            phoneUseType: "",
            primaryInd: false,
        },
        emailInfo: {
            id: "",
            type: "",
            email: ""
        },
        addressInfo: {
            id: "",
            type: "",
            addressLine: "",
            cityName: "",
            postalCode: "",
            country: "",
        },
        identificationInfo: {
            id: "",
            type: "",
            identification: {
                idType: "",
                idNumber: "",
            },
        },
        newsletter: false,
        completed: false,
    },
    guests: [],
    selectedRooms: [],
    currentStep: 0,
    isFormValid: false,
    isFormDirty: false,
    paymentEnabled: false,
    titles: {} as Record<string, string>,
    nationalities: {} as Record<string, string>,
    countries: {} as Record<string, string>,
};

export type AppAction =
    | { type: "SET_USER"; payload: GuestFormDataWithCompletion }
    | { type: "SET_GUESTS"; payload: GuestFormDataWithCompletion[] }
    | { type: "UPDATE_GUEST"; payload: GuestFormDataWithCompletion }
    | { type: "SET_SELECTED_ROOMS"; payload: string[] }
    | { type: "SET_STEP"; payload: number }
    | { type: "SET_STATE"; payload: Partial<AppState> }
    | { type: "SET_FORM_VALIDITY"; payload: boolean }
    | { type: "SET_FORM_DIRTY"; payload: boolean }
    | { type: "SET_PAYMENT_ENABLED"; payload: boolean }
    | { type: "SET_TITLES"; payload: Record<string, string> }
    | { type: "SET_NATIONALITIES"; payload: Record<string, string> }
    | { type: "SET_COUNTRIES"; payload: Record<string, string> };

function appReducer(state: AppState, action: AppAction): AppState {
    let newState: AppState;

    switch (action.type) {
        case "SET_USER":
            newState = { ...state, user: action.payload };
            break;
        case "SET_GUESTS":
            newState = { ...state, guests: action.payload };
            break;
        case "UPDATE_GUEST":
            newState = {
                ...state,
                guests: state.guests.map((guest) =>
                    guest.id === action.payload.id ? action.payload : guest
                ),
            };
            break;
        case "SET_SELECTED_ROOMS":
            newState = { ...state, selectedRooms: action.payload };
            break;
        case "SET_STEP":
            newState = { ...state, currentStep: action.payload };
            break;
        case "SET_STATE":
            newState = { ...state, ...action.payload };
            break;
        case "SET_FORM_VALIDITY":
            newState = { ...state, isFormValid: action.payload };
            break;
        case "SET_FORM_DIRTY":
            newState = { ...state, isFormDirty: action.payload };
            break;
        case "SET_PAYMENT_ENABLED":
            newState = { ...state, paymentEnabled: action.payload };
            break;
        case "SET_TITLES":
            return { ...state, titles: action.payload };
        case "SET_NATIONALITIES":
            return { ...state, nationalities: action.payload };
        case "SET_COUNTRIES":
            return { ...state, countries: action.payload };
        default:
            return state;
    }

    return newState;
}

interface AppContextProps {
    state: AppState;
    dispatch: React.Dispatch<AppAction>;
    updateGuest: (guest: GuestFormDataWithCompletion) => void;
    updateUser: (user: GuestFormDataWithCompletion) => void;
    setIsFormValid: (isValid: boolean) => void;
    setIsFormDirty: (isDirty: boolean) => void;
    setCurrentStep: (step: number) => void;
    setPaymentEnabled: (enabled: boolean) => void;
}

export const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);

    const updateGuest = (guest: GuestFormDataWithCompletion) => {
        dispatch({ type: "UPDATE_GUEST", payload: guest });
    };

    const updateUser = (user: GuestFormDataWithCompletion) => {
        dispatch({ type: "SET_USER", payload: user });
    };

    const setIsFormValid = (isValid: boolean) => {
        dispatch({ type: "SET_FORM_VALIDITY", payload: isValid });
    };

    const setIsFormDirty = (isDirty: boolean) => {
        dispatch({ type: "SET_FORM_DIRTY", payload: isDirty });
    };

    const setCurrentStep = (step: number) => {
        dispatch({ type: "SET_STEP", payload: step });
    };

    const setPaymentEnabled = (enabled: boolean) => {
        dispatch({ type: "SET_PAYMENT_ENABLED", payload: enabled });
    };

    // 🔁 Restore state on refresh
    useEffect(() => {
        const token = getToken();
        const expired = isTokenExpired();

        if (!token || expired) return;

        const lastName = sessionStorage.getItem("lastName");
        const confirmationNumber = sessionStorage.getItem("confirmationNumber");

        if (lastName && confirmationNumber) {
            loginAndFetchInitialData(lastName, confirmationNumber, dispatch).catch((err) => {
                console.error("Failed to rehydrate app context:", err);
            });
        }
    }, []);

    return (
        <AppContext.Provider
            value={{
                state,
                dispatch,
                updateGuest,
                updateUser,
                setIsFormValid,
                setIsFormDirty,
                setCurrentStep,
                setPaymentEnabled,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
